<template>
  <div class="article-detail-container">
    <!-- 返回按钮 -->
    <header class="detail-header">
      <button @click="goBack" class="back-btn">
        <i class="bi bi-arrow-left"></i>
        返回列表
      </button>
    </header>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <i class="bi bi-arrow-clockwise loading-icon"></i>
      <p>加载中...</p>
    </div>

    <!-- 文章内容 -->
    <div v-else-if="article" class="article-content">
      <!-- 文章标题 -->
      <h1 class="article-title">{{ article.title }}</h1>

      <!-- 文章元信息 -->
      <div class="article-meta">
        <div class="author-info">
          <i class="bi bi-person-circle"></i>
          <span>{{ article.author || '匿名作者' }}</span>
        </div>
        <div class="publish-time">
          <i class="bi bi-calendar"></i>
          <span>{{ formatDate(article.publishTime) }}</span>
        </div>
        <div class="view-count">
          <i class="bi bi-eye"></i>
          <span>{{ article.views }} 次阅读</span>
        </div>
      </div>

      <!-- 标签 -->
      <div class="article-tags" v-if="article.tags && article.tags.length">
        <span v-for="tag in article.tags" :key="tag" class="tag">
          {{ tag }}
        </span>
      </div>

      <!-- 文章内容 -->
      <div class="article-body">
        <p class="article-summary">{{ article.summary }}</p>

        <!-- 详细内容 -->
        <div class="article-detail-content">
          <h2>面试问题详解</h2>
          <div v-for="(qa, index) in article.questions" :key="index" class="qa-section">
            <h3>{{ index + 1 }}. {{ qa.question }}</h3>
            <div class="answer" v-html="qa.answer"></div>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="article-actions">
        <button class="action-btn like-btn" @click="toggleLike">
          <i :class="['bi', isLiked ? 'bi-heart-fill' : 'bi-heart']"></i>
          <span>{{ article.likes }} 赞</span>
        </button>
        <button class="action-btn collect-btn" @click="toggleCollect">
          <i :class="['bi', isCollected ? 'bi-bookmark-fill' : 'bi-bookmark']"></i>
          <span>{{ isCollected ? '已收藏' : '收藏' }}</span>
        </button>
      </div>
    </div>

    <!-- 404状态 -->
    <div v-else class="not-found-state">
      <i class="bi bi-file-earmark-x fs-1 text-muted"></i>
      <h3>文章不存在</h3>
      <p>该面经可能已被删除或不存在</p>
      <button @click="goBack" class="back-btn">返回列表</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArticleDetail',
  data() {
    return {
      loading: true,
      article: null,
      isLiked: false,
      isCollected: false
    }
  },
  computed: {
    /**
     * 从路由参数获取文章ID
     */
    articleId() {
      return parseInt(this.$route.params.id)
    }
  },
  methods: {
    /**
     * 加载文章详情数据
     */
    loadArticle() {
      // 模拟API调用
      setTimeout(() => {
        const articles = [
          // 文章1: HTML5+CSS3 现代布局技巧
          {
            id: 4,
            title: 'HTML5+CSS3 现代布局技巧完全指南',
            summary: '深入解析Flexbox、Grid布局系统，响应式设计最佳实践，移动端适配方案，以及CSS动画与过渡的高级技巧...',
            author: '前端架构师',
            views: 2341,
            likes: 156,
            publishTime: '2024-01-20T09:30:00',
            tags: ['HTML5', 'CSS3', '布局', '响应式'],
            questions: [
              {
                question: 'Flexbox和Grid布局如何选择？什么场景用哪个？',
                answer: `
                                    <p><strong>Flexbox适用场景：</strong></p>
                                    <ul>
                                        <li>一维布局（单行或单列）</li>
                                        <li>内容大小未知或动态变化</li>
                                        <li>需要灵活的空间分配</li>
                                    </ul>
                                    <p><strong>Grid适用场景：</strong></p>
                                    <ul>
                                        <li>二维布局（行列同时控制）</li>
                                        <li>复杂网格系统</li>
                                        <li>固定比例的区域划分</li>
                                    </ul>
                                    <div class="code-example">
                                        <h4>Flexbox示例：</h4>
                                        <pre><code>.flex-container {
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                              flex-wrap: wrap;
                            }

                            .flex-item {
                              flex: 1 1 200px;
                              margin: 10px;
                            }</code></pre>
                                    </div>
                                    <div class="code-example">
                                        <h4>Grid示例：</h4>
                                        <pre><code>.grid-container {
                              display: grid;
                              grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                              gap: 20px;
                            }</code></pre>
                                    </div>
                                `
              },
              {
                question: '如何实现移动端响应式布局？',
                answer: `
                                    <p><strong>响应式布局核心策略：</strong></p>
                                    <ol>
                                        <li><strong>媒体查询：</strong>根据不同屏幕尺寸应用不同样式</li>
                                        <li><strong>相对单位：</strong>使用rem、em、vw、vh代替固定像素</li>
                                        <li><strong>弹性布局：</strong>Flexbox和Grid的灵活应用</li>
                                        <li><strong>图片响应式：</strong>使用srcset和sizes属性</li>
                                    </ol>
                                    <div class="code-example">
                                        <h4>完整响应式布局示例：</h4>
                                        <pre><code>/* 移动端优先 */
                            .container {
                              width: 100%;
                              padding: 0 15px;
                            }

                            /* 平板 */
                            @media (min-width: 768px) {
                              .container {
                                max-width: 750px;
                                margin: 0 auto;
                              }
                            }

                            /* 桌面 */
                            @media (min-width: 1024px) {
                              .container {
                                max-width: 1200px;
                              }
                            }</code></pre>
                                    </div>
                                `
              }
            ]
          },

          // 文章2: Axios 完整教程
          {
            id: 5,
            title: 'Axios 从入门到精通的完整教程',
            summary: '全面讲解Axios的配置、拦截器、错误处理、并发请求、取消请求等高级功能，以及在实际项目中的最佳实践...',
            author: 'API大师',
            views: 1892,
            likes: 143,
            publishTime: '2024-01-19T14:20:00',
            tags: ['Axios', 'HTTP', 'JavaScript', '网络请求'],
            questions: [
              {
                question: '如何封装一个可复用的Axios实例？',
                answer: `
                                    <p><strong>封装Axios实例的核心要素：</strong></p>
                                    <div class="code-example">
                                        <pre><code>// http.js - 封装Axios实例
                              import axios from 'axios'

                              // 创建实例
                              const http = axios.create({
                                baseURL: process.env.VUE_APP_API_URL,
                                timeout: 10000,
                                headers: {
                                  'Content-Type': 'application/json'
                                }
                              })

                              // 请求拦截器
                              http.interceptors.request.use(
                                config => {
                                  // 添加token
                                  const token = localStorage.getItem('token')
                                  if (token) {
                                    config.headers.Authorization = \`Bearer \${token}\`
                                  }
                                  return config
                                },
                                error => {
                                  return Promise.reject(error)
                                }
                              )

                              // 响应拦截器
                              http.interceptors.response.use(
                                response => {
                                  // 统一处理响应数据
                                  return response.data
                                },
                                error => {
                                  // 统一错误处理
                                  if (error.response) {
                                    switch (error.response.status) {
                                      case 401:
                                        // 未授权
                                        router.push('/login')
                                        break
                                      case 500:
                                        // 服务器错误
                                        console.error('服务器错误')
                                        break
                                      default:
                                        console.error(error.response.data.message)
                                    }
                                  }
                                  return Promise.reject(error)
                                }
                              )

                              export default http</code></pre>
                                    </div>
                                `
              },
              {
                question: '如何处理并发请求和请求取消？',
                answer: `
                                    <p><strong>并发请求处理：</strong></p>
                                    <div class="code-example">
                                        <pre><code>// 并发请求示例
                              const getUserInfo = () => http.get('/user/info')
                              const getUserPosts = () => http.get('/user/posts')
                              const getUserComments = () => http.get('/user/comments')

                              // 使用Promise.all处理并发
                              Promise.all([getUserInfo(), getUserPosts(), getUserComments()])
                                .then(([userInfo, posts, comments]) => {
                                  console.log('所有数据获取完成')
                                })
                                .catch(error => {
                                  console.error('某个请求失败', error)
                                })

                              // 使用axios.all（Axios专用）
                              axios.all([getUserInfo(), getUserPosts()])
                                .then(axios.spread((userInfo, posts) => {
                                  console.log(userInfo, posts)
                                }))</code></pre>
                                    </div>
                                
                                <p><strong>请求取消功能：</strong></p>
                                <div class="code-example">
                                    <pre><code>// 创建取消令牌
                              const CancelToken = axios.CancelToken
                              const source = CancelToken.source()

                              // 发起可取消的请求
                              http.get('/api/data', {
                                cancelToken: source.token
                              })

                              // 取消请求
                              source.cancel('用户取消操作')

                              // 在组件中使用
                              export default {
                                data() {
                                  return {
                                    cancelTokenSource: null
                                  }
                                }
                                methods: {
                                  fetchData() {
                                    // 取消之前的请求
                                    if (this.cancelTokenSource) {
                                      this.cancelTokenSource.cancel()
                                    }
                                    
                                    // 创建新的取消令牌
                                    this.cancelTokenSource = CancelToken.source()
                                    
                                    http.get('/api/data', {
                                      cancelToken: this.cancelTokenSource.token
                                    }).then(data => {
                                      console.log('数据获取成功', data)
                                    })
                                  }
                                }
                                beforeDestroy() {
                                  // 组件销毁时取消请求
                                  if (this.cancelTokenSource) {
                                    this.cancelTokenSource.cancel()
                                  }
                                }
                              }</code></pre>
                                    </div>
                                `
              }
            ]
          },

          // 文章3: Vue3 组合式API
          {
            id: 6,
            title: 'Vue3 组合式API深度解析与实战',
            summary: '详细讲解Composition API的核心概念、响应式原理、自定义Hook开发，以及从Options API迁移的最佳实践...',
            author: 'Vue核心贡献者',
            views: 3215,
            likes: 267,
            publishTime: '2024-01-18T11:15:00',
            tags: ['Vue3', 'Composition API', '前端框架', '实战'],
            questions: [
              {
                question: 'Composition API的核心概念和优势是什么？',
                answer: `
                                    <p><strong>Composition API核心概念：</strong></p>
                                    <ul>
                                        <li><strong>setup()：</strong>所有组合式API的入口</li>
                                        <li><strong>响应式API：</strong>ref、reactive、computed、watch</li>
                                        <li><strong>生命周期钩子：</strong>onMounted、onUpdated等</li>
                                        <li><strong>逻辑复用：</strong>通过自定义Hook实现</li>
                                    </ul>
                                    
                                    <div class="code-example">
                                        <h4>基础使用示例：</h4>
                                        <pre><code>// 使用Composition API的组件示例
&lt;template&gt;
  &lt;div&gt;
    &lt;h1&gt;{{ count }}&lt;/h1&gt;
    &lt;button @click="increment"&gt;+1&lt;/button&gt;
    &lt;p&gt;双倍值: {{ doubleCount }}&lt;/p&gt;
  &lt;/div&gt;
&lt;/template&gt;

&lt;script&gt;
import { ref, computed, onMounted } from 'vue'

export default {
  setup() {
    // 响应式数据
    const count = ref(0)
    
    // 计算属性
    const doubleCount = computed(() => count.value * 2)
    
    // 方法
    const increment = () => count.value++
    
    // 生命周期钩子
    onMounted(() => {
      console.log('组件已挂载')
    })
    
    return {
      count,
      doubleCount,
      increment
    }
  }
}
&lt;/script&gt;</code></pre>
                                    </div>
                                `
              },
              {
                question: '如何创建可复用的自定义Hook？',
                answer: `
                                    <p><strong>自定义Hook最佳实践：</strong></p>
                                    <div class="code-example">
                                        <h4>useCounter Hook示例：</h4>
                                        <pre><code>// hooks/useCounter.js
                              import { ref, computed } from 'vue'

                              export function useCounter(initialValue = 0) {
                                const count = ref(initialValue)
                                
                                const increment = () => count.value++
                                const decrement = () => count.value--
                                const reset = () => count.value = initialValue
                                
                                const doubleCount = computed(() => count.value * 2)
                                
                                return {
                                  count,
                                  increment,
                                  decrement,
                                  reset,
                                  doubleCount
                                }
                              }</code></pre>
                                    </div>
                                    
                                    <div class="code-example">
                                        <h4>使用自定义Hook：</h4>
                                        <pre><code>// 在组件中使用
&lt;script&gt;
import { useCounter } from '@/hooks/useCounter'

export default {
  setup() {
    const { count, increment, doubleCount } = useCounter(10)
    
    return {
      count,
      increment,
      doubleCount
    }
  }
}
&lt;/script&gt;</code></pre>
                                    </div>
                                `
              }
            ]
          },

          // 文章4: JavaScript 异步编程
          {
            id: 7,
            title: 'JavaScript 异步编程从回调到Async/Await',
            summary: '系统梳理JS异步编程的演进历程：回调函数、Promise、Generator、Async/Await，以及错误处理策略...',
            author: 'JS专家',
            views: 1654,
            likes: 198,
            publishTime: '2024-01-17T16:45:00',
            tags: ['JavaScript', '异步编程', 'Promise', 'Async/Await'],
            questions: [
              {
                question: 'Promise的三种状态和链式调用如何实现？',
                answer: `
                                    <p><strong>Promise的三种状态：</strong></p>
                                    <ul>
                                        <li><strong>pending：</strong>进行中</li>
                                        <li><strong>fulfilled：</strong>已成功</li>
                                        <li><strong>rejected：</strong>已失败</li>
                                    </ul>
                                    
                                    <div class="code-example">
                                        <h4>Promise链式调用示例：</h4>
                                        <pre><code>// Promise链式调用
                              fetchUserData(userId)
                                .then(user => {
                                  console.log('用户信息:', user)
                                  return fetchUserPosts(user.id)
                                })
                                .then(posts => {
                                  console.log('用户文章:', posts)
                                  return fetchPostComments(posts[0].id)
                                })
                                .then(comments => {
                                  console.log('文章评论:', comments)
                                })
                                .catch(error => {
                                  console.error('发生错误:', error)
                                })

                              // Promise.all处理多个异步操作
                              Promise.all([
                                fetchUserData(userId),
                                fetchUserPosts(userId),
                                fetchUserSettings(userId)
                              ]).then(([user, posts, settings]) => {
                                console.log('所有数据获取完成')
                                // 处理数据
                              }).catch(error => {
                                console.error('某个请求失败:', error)
                              })</code></pre>
                                    </div>
                                `
              },
              {
                question: 'Async/Await的错误处理最佳实践是什么？',
                answer: `
                                    <p><strong>Async/Await错误处理策略：</strong></p>
                                    <div class="code-example">
                                        <h4>使用try-catch处理错误：</h4>
                                        <pre><code>// 基础错误处理
                              async function fetchUserData(userId) {
                                try {
                                  const user = await api.getUser(userId)
                                  const posts = await api.getUserPosts(user.id)
                                  return { user, posts }
                                } catch (error) {
                                  console.error('获取用户数据失败:', error)
                                  throw error
                                }
                              }

                              // 多个异步操作的错误处理
                              async function fetchAllData(userId) {
                                try {
                                  const [user, posts, settings] = await Promise.all([
                                    api.getUser(userId),
                                    api.getUserPosts(userId),
                                    api.getUserSettings(userId)
                                  ])
                                  return { user, posts, settings }
                                } catch (error) {
                                  // 处理错误
                                  if (error.response?.status === 404) {
                                    console.log('用户不存在')
                                  } else {
                                    console.log('网络错误')
                                  }
                                  throw error
                                }
                              }

                              // 使用finally清理资源
                              async function loadData() {
                                try {
                                  const data = await fetchUserData(userId)
                                  return data
                                } catch (error) {
                                  console.error(error)
                                } finally {
                                  // 无论成功失败都会执行
                                  hideLoading()
                                }
                              }</code></pre>
                                    </div>
                                `
              }
            ]
          },

          // 文章5: TypeScript 最佳实践
          {
            id: 8,
            title: 'TypeScript 在Vue3项目中的最佳实践',
            summary: 'TypeScript与Vue3完美结合：类型定义、组件props类型、事件类型、Composition API类型推断等高级用法...',
            author: '类型体操大师',
            views: 1423,
            likes: 176,
            publishTime: '2024-01-16T13:30:00',
            tags: ['TypeScript', 'Vue3', '类型系统', '最佳实践'],
            questions: [
              {
                question: 'Vue3中如何定义组件的props和emit类型？',
                answer: `
                                    <p><strong>使用TypeScript定义组件类型：</strong></p>
                                    <div class="code-example">
                                        <h4>定义Props和Emit类型：</h4>
                                        <pre><code>// 定义props接口
                              interface Props {
                                title: string
                                count?: number
                                user: User
                                items: Item[]
                              }

                              // 定义emit类型
                              interface Emits {
                                update: [value: number]
                                delete: [id: string]
                                submit: []
                              }

                              // 在组件中使用
                              &lt;script setup lang="ts"&gt;
                              interface Props {
                                title: string
                                count?: number
                                items: Array&lt;{ id: number; name: string }&gt;
                              }

                              interface Emits {
                                (e: 'update:count', value: number): void
                                (e: 'delete', id: number): void
                              }

                              const props = withDefaults(defineProps&lt;Props&gt;(), {
                                count: 0
                              })

                              const emit = defineEmits&lt;Emits&gt;()

                              // 使用
                              const handleUpdate = (value: number) => {
                                emit('update:count', value)
                              }
                              &lt;/script&gt;</code></pre>
                                    </div>
                                `
              },
              {
                question: '如何在Composition API中使用泛型？',
                answer: `
                                    <p><strong>Composition API中的泛型使用：</strong></p>
                                    <div class="code-example">
                                        <h4>泛型Hook示例：</h4>
                                        <pre><code>// 泛型useFetch Hook
                              import { ref, onMounted } from 'vue'

                              interface FetchState&lt;T&gt; {
                                data: T | null
                                loading: boolean
                                error: Error | null
                              }

                              export function useFetch&lt;T&gt;(url: string) {
                                const state = ref&lt;FetchState&lt;T&gt;&gt;({
                                  data: null,
                                  loading: false,
                                  error: null
                                })

                                const fetchData = async () => {
                                  state.value.loading = true
                                  try {
                                    const response = await fetch(url)
                                    const data = await response.json()
                                    state.value.data = data
                                  } catch (error) {
                                    state.value.error = error as Error
                                  } finally {
                                    state.value.loading = false
                                  }
                                }

                                onMounted(() => {
                                  fetchData()
                                })

                                return {
                                  ...toRefs(state.value),
                                  refetch: fetchData
                                }
                              }

                              // 使用泛型Hook
                              const { data, loading, error } = useFetch&lt;User&gt;('/api/user/1')</code></pre>
                                    </div>
                                `
              }
            ]
          }
        ]
        // 通过id渲染文章详情

        this.article = articles.find(a => a.id === this.articleId)
        this.loading = false
      }, 500)
    },

    /**
     * 返回列表页
     */
    goBack() {
      this.$router.push('/article')
    },

    /**
     * 切换点赞状态
     */
    toggleLike() {
      this.isLiked = !this.isLiked
      this.article.likes += this.isLiked ? 1 : -1
      // 这里可以添加API调用
      console.log('点赞状态：', this.isLiked)
    },

    /**
     * 切换收藏状态
     */
    toggleCollect() {
      this.isCollected = !this.isCollected
      // 这里可以添加API调用
      console.log('收藏状态：', this.isCollected)
    },

    /**
     * 格式化日期
     */
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  // 当ArticleData页面的面经列表被点击时候 会跳转到这个ArticleDetail页面，
  // 此时在组件创建完成后，自动调用loadArticle方法来加载对应文章的详情数据，
  // 以展示当前路由参数中指定ID的文章内容。
  created() {
    this.loadArticle()
  },
  activated() {
    console.log("组件激活了");
  },
  deactivated() {
    console.log("组件失活了");
  },
}
</script>

<style scoped>
.article-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
}

.detail-header {
  margin-bottom: 20px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.loading-icon {
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.article-content {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.article-title {
  color: #1a1a1a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 20px;
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #666;
}

.author-info,
.publish-time,
.view-count {
  display: flex;
  align-items: center;
  gap: 6px;
}

.article-tags {
  margin-bottom: 30px;
}

.tag {
  background: #f0f7ff;
  color: #1890ff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  margin-right: 8px;
}

.article-body {
  line-height: 1.8;
}

.article-summary {
  font-size: 16px;
  color: #333;
  margin-bottom: 30px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
}

.article-detail-content h2 {
  color: #1a1a1a;
  font-size: 22px;
  margin: 30px 0 20px;
}

.qa-section {
  margin-bottom: 25px;
}

.qa-section h3 {
  color: #1a1a1a;
  font-size: 18px;
  margin-bottom: 15px;
}

.answer {
  color: #333;
  font-size: 15px;
  line-height: 1.7;
}

.answer :deep(ul),
.answer :deep(ol) {
  margin: 10px 0;
  padding-left: 20px;
}

.answer :deep(li) {
  margin: 5px 0;
}

.article-actions {
  display: flex;
  gap: 20px;
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #eee;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: #f5f5f5;
}

.like-btn:hover {
  border-color: #ff4757;
  color: #ff4757;
}

.collect-btn:hover {
  border-color: #ffa502;
  color: #ffa502;
}

.not-found-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.not-found-state h3 {
  margin: 15px 0 10px;
  color: #666;
}



/* 在现有样式基础上添加代码块样式 */
.code-example {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.code-example h4 {
  color: #495057;
  font-size: 16px;
  margin-bottom: 15px;
}

.code-example pre {
  background: #2d3748;
  color: #e2e8f0;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 14px;
  line-height: 1.5;
}

.code-example code {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

/* 美化列表样式 */
.article-body :deep(ul),
.article-body :deep(ol) {
  margin: 15px 0;
  padding-left: 30px;
}

.article-body :deep(li) {
  margin: 8px 0;
  line-height: 1.6;
}

.article-body :deep(strong) {
  color: #2d3748;
  font-weight: 600;
}

/* 响应式代码块 */
@media (max-width: 768px) {
  .code-example pre {
    font-size: 13px;
    padding: 12px;
  }
}
</style>
